/**
 * Enhanced Recommendation Letter Framework
 * 
 * Strategic, authentic templates and guidance for college recommendation letters
 * implementing a two-step approach: brief initial request + detailed follow-up
 */

// Updated email templates with authentic tone and two-step approach
const emailTemplates = {
  teacher: {
    subject: "Recommendation letter request - {{YOUR_NAME}}",
    body: `Hi {{TEACHER_NAME}},

I've been thinking about who knows me best as a student, and your {{SUBJECT}} class keeps coming to mind. I still think about {{SPECIFIC_MEMORY}}.

I'm starting my college applications and would be honored if you'd consider writing a recommendation letter for me. I believe you've seen me {{GROWTH_MOMENT}}, and I think you understand {{ACADEMIC_STRENGTH}} that really defines how I approach learning.

{{CHARACTER_TRAIT}} is something I value about myself, and I believe you've witnessed this in your classroom. My main deadlines are in {{MONTH}}, which gives us plenty of time to do this properly. I have my brag sheet, college list, and personal statement ready to share if you're willing to help.

Would you be available for a brief conversation after class sometime this week? I'd love to tell you more about my college goals and provide you with all the materials you'd need.

I understand that writing recommendations requires significant time and thought, so please don't hesitate to let me know if you're unable to take this on.

Thank you for considering my request, and for everything you've taught me this year.

Best regards,
{{YOUR_NAME}}`,
    followUp: true
  },
  
  teacher_follow_up: {
    subject: "Materials for recommendation letter - {{YOUR_NAME}}",
    body: `Hi {{TEACHER_NAME}},

Thank you so much for agreeing to write my recommendation letter! As promised, here are all the materials you requested:

**Attached documents:**
- Brag sheet/activity resume
- Personal statement draft  
- College list with deadlines
- Any specific forms or submission instructions

**Quick context that might be helpful:**
I'm planning to study {{INTENDED_MAJOR}} because your {{SUBJECT}} class helped me realize {{SPECIFIC_MEMORY}}. This experience showed me {{GROWTH_MOMENT}}.

**Things you've observed that others might not know:**
- {{ACADEMIC_STRENGTH}} that defines how I approach learning
- {{CHARACTER_TRAIT}} that you've witnessed in your classroom
- My ability to {{GROWTH_MOMENT}} when facing challenges

I know you understand my academic abilities, but I think you've also seen these personal qualities that really define how I tackle challenges.

**Key deadline:** {{DUE_DATE}} - but please let me know if you need anything else or if there's a better timeline for you.

Thank you again for supporting my college goals. It means a lot to have your perspective as part of my applications.

Best regards,
{{YOUR_NAME}}`,
    isFollowUp: true
  },

  counselor: {
    subject: "College rec letter request",
    body: `Hi {{COUNSELOR_NAME}},

Hope your {{CURRENT_SEASON}} is going well! I wanted to ask if you'd be willing to write my counselor recommendation for college applications.

I know you see a lot of students, but I'm hoping our conversations about {{PERSONAL_DISCUSSION}} gave you a sense of who I am beyond my transcript.

I'm applying to {{COLLEGE_COUNT}} schools, mostly focused on {{COLLEGE_FOCUS}}. The big picture is that I want to study {{INTENDED_MAJOR}} and {{COLLEGE_EXCITEMENT}}.

What might be helpful for you to know:
- The thing I'm most proud of from high school: {{PROUD_ACHIEVEMENT}}
- Biggest challenge I've worked through: {{BIGGEST_CHALLENGE}}
- What I'm actually excited about for college: {{COLLEGE_EXCITEMENT}}

Deadlines are in {{MONTH}}, so plenty of time. Would it be helpful to meet for a few minutes to talk through my college list and what I'm hoping to get out of the next four years?

Thanks for everything you do - I know you're juggling a lot.

Best,
{{YOUR_NAME}}`
  },

  counselor_follow_up: {
    subject: "Materials for counselor recommendation - {{YOUR_NAME}}",
    body: `Hi {{COUNSELOR_NAME}},

Thank you for agreeing to write my counselor recommendation! Here are all the materials and context you requested:

**Attached documents:**
- Complete brag sheet/activity resume
- Personal statement draft
- Detailed college list with deadlines and reasons for each choice
- Official transcript (if you don't have access)
- Any supplemental forms

**Personal context you should know:**
- What I'm most proud of from high school: {{PROUD_ACHIEVEMENT}}
- Biggest challenge I've worked through: {{BIGGEST_CHALLENGE}}
- Our conversations about: {{PERSONAL_DISCUSSION}}
- What I'm excited about for college: {{COLLEGE_EXCITEMENT}}

**College fit reasoning:**
I've chosen these schools because {{COLLEGE_EXCITEMENT}} and I want to study {{INTENDED_MAJOR}}.

**Timeline:**
- Key deadline: {{DUE_DATE}}
- Happy to meet again if you need clarification on anything

I really appreciate you taking the time to provide the school counselor perspective on my application. Your support means a lot.

Best,
{{YOUR_NAME}}`,
    isFollowUp: true
  },

  coach_advisor: {
    subject: "Recommendation letter request - {{YOUR_NAME}}",
    body: `Hi {{COACH_NAME}},

I've been working on college applications and realized that you've probably seen sides of me that don't show up in the classroom. Would you be willing to write a recommendation letter for me?

Being part of {{SUBJECT}} has been really meaningful to me. I think you've seen me {{LEADERSHIP_EXAMPLE}}, and I believe you understand {{TEAM_CONTRIBUTION}} that defines how I approach challenges.

{{PERSONAL_GROWTH}} is something I've developed through our work together, and I think you've witnessed {{CHARACTER_MOMENT}} that really shows who I am as a person.

I'm applying to study {{INTENDED_MAJOR}} in college, and I think your perspective on my leadership and character would add something important that my teachers can't speak to. My main deadlines are in {{MONTH}}, which gives us plenty of time to do this properly.

I have my brag sheet, college list, and personal statement ready to share if you're willing to help. Would you be available for a brief conversation sometime this week?

I understand that writing recommendations requires significant time and thought, so please don't hesitate to let me know if you're unable to take this on.

Thank you for considering my request, and for everything you've taught me through {{SUBJECT}}.

Best regards,
{{YOUR_NAME}}`
  },

  coach_advisor_follow_up: {
    subject: "Materials for recommendation letter - {{YOUR_NAME}}",
    body: `Hi {{COACH_NAME}},

Thanks so much for agreeing to write my recommendation letter! Here's everything you need:

**Attached documents:**
- Brag sheet highlighting my involvement in {{SUBJECT}}
- Personal statement draft
- College list and deadlines
- Any specific forms or submission instructions

**Context about my college plans:**
I'm planning to study {{INTENDED_MAJOR}} because my experience with {{SUBJECT}} has taught me {{PERSONAL_GROWTH}} that I want to build on in college.

**What you've seen that others haven't:**
- {{LEADERSHIP_EXAMPLE}} that demonstrates my ability to lead
- {{TEAM_CONTRIBUTION}} showing how I work with others
- {{CHARACTER_MOMENT}} that reveals my character under pressure
- {{PERSONAL_GROWTH}} that you've witnessed over time

**Why your perspective matters:**
While my teachers can speak to my academic abilities, you've seen me {{TEAM_CONTRIBUTION}} and handle real leadership challenges. I think this shows {{CHARACTER_MOMENT}} that's really important for college admissions committees to understand.

**Key deadline:** {{DUE_DATE}}

Thank you for all the ways you've helped me grow through {{SUBJECT}}. Having your voice as part of my college applications means a lot.

Best,
{{YOUR_NAME}}`,
    isFollowUp: true
  },

  employer_mentor: {
    subject: "College recommendation request - {{YOUR_NAME}}",
    body: `Hi {{MENTOR_NAME}},

I hope things are going well at {{SUBJECT}}. I'm starting my college applications and was wondering if you'd be willing to write a recommendation letter for me.

Working with you has been different from school in the best way. I think you've seen me {{WORK_INITIATIVE}} and demonstrate {{RELIABILITY_EXAMPLE}} that really shows my work ethic.

{{PROBLEM_SOLVING}} is something I've developed through our work together, and I believe you understand {{PROFESSIONAL_GROWTH}} that defines how I approach real-world challenges.

I'm planning to study {{INTENDED_MAJOR}} in college, and honestly, a lot of my interest in this comes from {{WORK_INITIATIVE}} that I've experienced working with you. I think your perspective on how I handle responsibility outside of the classroom would really add to my applications.

My main deadlines are in {{MONTH}}, which gives us plenty of time to do this properly. I have my resume, college list, and personal statement ready to share if you're willing to help.

I understand that writing recommendations requires significant time and thought, so please don't hesitate to let me know if you're unable to take this on.

Thank you for considering my request, and for everything you've taught me about {{PROFESSIONAL_GROWTH}}.

Best regards,
{{YOUR_NAME}}`
  },

  employer_mentor_follow_up: {
    subject: "Materials for recommendation letter - {{YOUR_NAME}}",
    body: `Hi {{MENTOR_NAME}},

Thank you for agreeing to write my recommendation letter! Here are the materials and context that will help:

**Attached documents:**
- Brag sheet/resume including my work with you
- Personal statement draft
- College list with deadlines
- Any specific forms or submission instructions

**About my college goals:**
I'm applying to study {{INTENDED_MAJOR}} because my experience working with you opened my eyes to {{WORK_INITIATIVE}}. I've learned {{PROFESSIONAL_GROWTH}} that I want to develop further in college.

**What makes our work together unique:**
Unlike school, where I'm mostly being evaluated, working with you has let me {{WORK_INITIATIVE}} and take real responsibility. You've seen me {{PROBLEM_SOLVING}} and demonstrate {{RELIABILITY_EXAMPLE}} in professional situations.

**Examples you might want to reference:**
- {{WORK_INITIATIVE}} where I took ownership and delivered results
- {{PROBLEM_SOLVING}} that shows my analytical and creative thinking
- {{PROFESSIONAL_GROWTH}} that demonstrates my maturity and development
- {{RELIABILITY_EXAMPLE}} that proves my consistency and work ethic

**College fit:**
I'm drawn to programs that will build on {{PROFESSIONAL_GROWTH}} because I want to {{WORK_INITIATIVE}} in my future career. Your perspective on my readiness for independence and leadership would be valuable.

**Key deadline:** {{DUE_DATE}}

I'm grateful for everything I've learned working with you. Thank you for supporting my next step!

Best,
{{YOUR_NAME}}`,
    isFollowUp: true
  },

  // Thank You Note Templates
  teacher_thank_you: {
    subject: "Thank you for your recommendation letter - {{YOUR_NAME}}",
    body: `Hi {{TEACHER_NAME}},

I wanted to reach out and thank you again for writing my recommendation letter. {{OUTCOME_UPDATE}}

Your letter was such an important part of my application, and I'm grateful for the time and thought you put into it. {{SPECIFIC_APPRECIATION}}

{{IMPACT_STATEMENT}}

I'll always remember {{MEMORABLE_MOMENT}} from your class, and I'm excited to carry forward everything you taught me about {{SUBJECT_IMPACT}}.

Thank you again for believing in me and supporting my college goals.

With gratitude,
{{YOUR_NAME}}`,
    isThankYou: true
  },

  counselor_thank_you: {
    subject: "Thank you for your support - {{YOUR_NAME}}",
    body: `Hi {{COUNSELOR_NAME}},

I wanted to share some exciting news and thank you for all your support throughout this process! {{OUTCOME_UPDATE}}

Your counselor recommendation was such an important part of my application package. {{SPECIFIC_APPRECIATION}} I really appreciated how you {{COUNSELING_SUPPORT}} and helped me navigate this whole process.

{{IMPACT_STATEMENT}}

Looking back on our meetings about {{PERSONAL_DISCUSSION}}, I'm grateful for your guidance in helping me find schools that were truly the right fit.

Thank you for everything you've done to support my college journey!

Best,
{{YOUR_NAME}}`,
    isThankYou: true
  },

  coach_advisor_thank_you: {
    subject: "Thank you for your recommendation - {{YOUR_NAME}}",
    body: `Hi {{COACH_NAME}},

I have some great news to share with you! {{OUTCOME_UPDATE}}

Thank you so much for writing my recommendation letter. Your perspective on my {{LEADERSHIP_EXAMPLE}} and {{CHARACTER_MOMENT}} really helped admissions committees see who I am beyond just grades and test scores.

{{SPECIFIC_APPRECIATION}}

{{IMPACT_STATEMENT}}

I'll always be grateful for everything {{SUBJECT}} taught me about {{PERSONAL_GROWTH}} and teamwork. I know these lessons will serve me well in college and beyond.

Thank you for being such an important mentor in my life.

With appreciation,
{{YOUR_NAME}}`,
    isThankYou: true
  },

  employer_mentor_thank_you: {
    subject: "Thank you for your recommendation - {{YOUR_NAME}}",
    body: `Hi {{MENTOR_NAME}},

I wanted to share some wonderful news with you! {{OUTCOME_UPDATE}}

Thank you so much for taking the time to write my recommendation letter. Your insights about my {{WORK_INITIATIVE}} and {{PROFESSIONAL_GROWTH}} provided such a valuable perspective that my teachers and counselor couldn't offer.

{{SPECIFIC_APPRECIATION}}

{{IMPACT_STATEMENT}}

Working with you taught me so much about {{PROFESSIONAL_GROWTH}} and gave me confidence that I can succeed in the real world. I'm excited to build on these skills in college.

Thank you for being an incredible mentor and for supporting my next chapter.

With sincere gratitude,
{{YOUR_NAME}}`,
    isThankYou: true
  }
};

// Strategic guidance for the recommendation process
const recommendationStrategy = {
  timing: {
    ideal_request_time: "6-8 weeks before deadlines",
    avoid_periods: ["Right before breaks", "During testing periods", "End of semester crunch"],
    follow_up_schedule: ["2 weeks after initial request", "1 week before deadline"],
    emergency_timeline: "3 weeks minimum (with apology for short notice)"
  },
  
  selection_criteria: {
    academic_recommenders: {
      priority: "Teachers who've seen you think and grow, not just perform",
      ideal_subjects: "Core academic subjects (English, Math, Science, History)",
      relationship_strength: "At least one full semester, preferably full year",
      avoid: "Teachers who only know you from large lectures or minimal interaction"
    },
    character_recommenders: {
      types: ["School counselor", "Coach", "Activity advisor", "Employer", "Mentor"],
      value_add: "Different perspective from academic - leadership, character, real-world skills",
      selection_tip: "Choose someone who's seen you handle responsibility or overcome challenges"
    }
  },

  materials_to_provide: {
    essential: [
      "Brag sheet/activity resume",
      "Personal statement draft", 
      "College list with deadlines",
      "Specific examples of growth/achievements they witnessed"
    ],
    helpful: [
      "Transcript (if counselor doesn't have access)",
      "Writing samples from their class",
      "Photos from activities they supervised",
      "Thank you note after submission"
    ]
  },

  common_mistakes: [
    "Asking too late (less than 4 weeks notice)",
    "Choosing based on grades rather than relationship strength", 
    "Not providing specific context about your goals",
    "Forgetting to follow up appropriately",
    "Asking too many people from same area (all teachers, no character refs)"
  ],

  status_workflow: {
    'potential': {
      label: 'Setup Required',
      description: 'Quick entry needs completion',
      next_actions: ['Complete recommender details', 'Start Smart Request'],
      color: 'amber',
      step: 0,
      progress: 0
    },
    'not_requested': {
      label: 'Draft Request',
      description: 'Ready to draft initial request',
      next_actions: ['Draft request with AI', 'Use email template'],
      color: 'azure',
      step: 1,
      progress: 25
    },
    'ready_to_send': {
      label: 'Ready to Send',
      description: 'Request drafted, ready to send',
      next_actions: ['Review and send email'],
      color: 'indigo',
      step: 2,
      progress: 50
    },
    'initial_sent': {
      label: 'Request Sent',
      description: 'Waiting for response to initial request',
      next_actions: ['Prepare follow-up materials', 'Wait for response'],
      color: 'violet',
      step: 2,
      progress: 50
    },
    'in_progress': {
      label: 'Following Up',
      description: 'Send materials and track progress',
      next_actions: ['Send brag sheet', 'Check deadline status'],
      color: 'indigo',
      step: 3,
      progress: 75
    },
    'materials_sent': {
      label: 'Materials Sent',
      description: 'All materials provided, awaiting letter',
      next_actions: ['Send reminder if needed', 'Track submission'],
      color: 'violet',
      step: 3,
      progress: 75
    },
    'submitted': {
      label: 'Complete',
      description: 'Letter received - send thank you!',
      next_actions: ['Send thank you note', 'Update college checklist'],
      color: 'emerald',
      step: 4,
      progress: 100
    }
  }
};

// Enhanced template selection logic
const getRecommendedTemplate = (recommenderType, relationshipStrength, hasMetBefore) => {
  const templates = {
    teacher: 'teacher',
    counselor: 'counselor',
    coach: 'coach_advisor',
    advisor: 'coach_advisor',
    employer: 'employer_mentor',
    mentor: 'employer_mentor',
    other: 'teacher' // Default fallback
  };

  return templates[recommenderType] || templates.teacher;
};

// Copy template with strategic guidance
const copyTemplateWithGuidance = (templateKey, recommenderInfo = {}) => {
  const template = emailTemplates[templateKey];
  
  if (!template) {
    console.error('Template not found:', templateKey);
    return;
  }

  // Create customized version with placeholder guidance
  let customizedBody = template.body;
  
  // Add contextual guidance based on recommender type
  const guidanceNotes = {
    teacher: `✅ Template copied! 

Key customizations needed:
• Replace [Specific memory] with real class moment
• Use actual class name for [Subject]
• Add real [Growth example] they witnessed

💡 This is Step 1 - after they agree, send materials!`,

    counselor: `✅ Template copied! 

Key customizations needed:
• Reference current [season] at school
• Mention real [conversation] you've had
• Use actual number of schools

💡 Focus on your "whole student" story beyond grades!`,

    coach_advisor: `✅ Template copied! 

Key customizations needed:
• Use specific [team/activity] name
• Add real [leadership moment] they saw
• Describe actual [impact] on you

💡 Highlight character qualities they've observed!`,

    employer_mentor: `✅ Template copied! 

Key customizations needed:
• Use how you normally address them
• Add specific [growth moment] from work
• Connect to your college goals

💡 Emphasize real-world maturity they've seen!`
  };

  // Determine base template type for guidance
  const baseKey = templateKey.includes('_follow_up') ? templateKey.replace('_follow_up', '') : templateKey;
  const guidance = guidanceNotes[baseKey] || guidanceNotes.teacher;

  navigator.clipboard.writeText(customizedBody);
  
  // Show strategic guidance in a more user-friendly way
  if (window.confirm) {
    setTimeout(() => {
      alert(guidance);
    }, 100);
  }
  
  return template;
};

// Two-step process guidance
const getTwoStepGuidance = (step, recommenderType) => {
  const baseTemplate = getRecommendedTemplate(recommenderType);
  
  if (step === 1) {
    return {
      title: "Step 1: Initial Request",
      description: "Keep it brief and respectful. Focus on the relationship and your request.",
      template: baseTemplate,
      nextStep: "After they agree, immediately send follow-up with materials",
      tips: [
        "Be specific about your relationship with them",
        "Give them adequate timeline (6+ weeks is ideal)",
        "Mention you have materials ready to share",
        "Make it easy for them to say no if they're too busy"
      ]
    };
  } else {
    return {
      title: "Step 2: Materials & Context", 
      description: "Provide everything they need to write a strong letter.",
      template: `${baseTemplate}_follow_up`,
      materials: recommendationStrategy.materials_to_provide.essential,
      tips: [
        "Send this immediately after they agree",
        "Include specific examples they witnessed",
        "Explain your college goals and fit",
        "Give them concrete deadline information"
      ]
    };
  }
};

// Timeline analysis helper
const getTimelineWarning = (dueDate) => {
  if (!dueDate) return null;
  
  const now = new Date();
  const deadline = new Date(dueDate);
  const weeksUntilDue = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24 * 7));
  
  if (weeksUntilDue < 0) {
    return { 
      type: 'error', 
      message: 'This deadline has passed. You may need to contact colleges about extensions.',
      action: 'Contact admissions offices immediately'
    };
  } else if (weeksUntilDue < 3) {
    return { 
      type: 'error', 
      message: 'This is very short notice. Consider apologizing for the timing and offering to provide all materials immediately.',
      action: 'Apologize for short notice, provide all materials upfront'
    };
  } else if (weeksUntilDue < 6) {
    return { 
      type: 'warning', 
      message: 'This is getting tight. Request as soon as possible and be extra prepared with materials.',
      action: 'Send request today, have all materials ready'
    };
  } else {
    return { 
      type: 'success', 
      message: 'Good timing - plenty of notice for a quality letter.',
      action: 'Proceed with standard timeline'
    };
  }
};

// Enhanced component integration helpers
const enhancedTemplateModal = {
  showStrategicGuidance: true,
  includeFollowUpTemplates: true,
  provideTwoStepWorkflow: true,
  customizationHelp: true
};

// Recommender selection helper
const getRecommenderSelectionGuidance = () => {
  return {
    academic: {
      title: "Academic Recommenders (Teachers)",
      tips: [
        "Choose teachers who've seen you think and grow, not just get good grades",
        "Prioritize core subjects: English, Math, Science, History",
        "Select someone you've had for at least a full semester",
        "Avoid large lecture classes where you had minimal interaction"
      ],
      ideal: "Teacher who saw you overcome challenges, ask thoughtful questions, or show genuine intellectual curiosity"
    },
    character: {
      title: "Character Recommenders (Coaches, Advisors, Employers)",
      tips: [
        "Choose someone who's seen you in leadership or responsibility roles",
        "Look for people who've observed your work ethic and character",
        "Consider supervisors from jobs, volunteer work, or activities",
        "Pick someone who's seen you handle challenges or conflict"
      ],
      ideal: "Adult who's watched you grow, take initiative, or demonstrate maturity outside the classroom"
    },
    counselor: {
      title: "School Counselor",
      tips: [
        "Usually required - they provide the 'whole student' perspective",
        "They can explain context like schedule changes, family situations, etc.",
        "Give them comprehensive information about your goals and challenges",
        "Meet with them regularly throughout junior/senior year if possible"
      ],
      ideal: "Counselor who understands your academic journey, personal growth, and college fit"
    }
  };
};

export { 
  emailTemplates, 
  recommendationStrategy, 
  getRecommendedTemplate, 
  copyTemplateWithGuidance,
  getTwoStepGuidance,
  getTimelineWarning,
  getRecommenderSelectionGuidance,
  enhancedTemplateModal
};